import React, { useState, useEffect } from 'react';
import {
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  CircularProgress,
  Box
} from '@mui/material';
import { getDepartureWindowOptions } from '../utils/departureWindowOptions';
import { Bus, RouteType } from '../types';
import { User } from '../../../types/userType';
import DialogModal from '../../../components/DialogModal';
import { SnackbarSeverityType } from '../../../enums';
import { upsertRoute } from '../../../apis/transport/routes';
import DropdownSelect, {
  DropdownSelectOption
} from '../../../components/DropdownSelect/DropdownSelect';
import TextInputWithIcon from '../../../components/TextInputWithIcon';
import pallet from '../../../layouts/pallet';
import { StyledRadioButton } from '../../common/styled';

interface AddRouteDialogProps {
  open: boolean;
  onClose: () => void;
  onAddRoute: () => void;
  loggedInUser: User;
  showToast: (message: string, severity: SnackbarSeverityType) => void;
  buses: Bus[];
}

const AddRouteDialog: React.FC<AddRouteDialogProps> = ({
  open,
  onClose,
  onAddRoute,
  loggedInUser,
  showToast,
  buses
}) => {
  const [name, setName] = useState('');
  const [departureWindow, setDepartureWindow] = useState('');
  const [busId, setBusId] = useState<string | null>(null);
  const [type, setType] = useState<RouteType | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const departureWindowOptions = getDepartureWindowOptions();
  const [busOptions, setBusOptions] = useState<DropdownSelectOption[]>([]);

  useEffect(() => {
    if (open) {
      setName('');
      setDepartureWindow('');
      setBusId(null);
      setType(null);
      setIsLoading(false);
    }
  }, [open]);

  useEffect(() => {
    if (buses) {
      setBusOptions(buses.map((bus) => ({ value: bus.id, label: bus.name })));
    }
  }, [buses]);

  const handleAddRoute = async () => {
    if (!type) return;
    setIsLoading(true);
    try {
      const [from, to] = departureWindow.split(' and ');
      await upsertRoute(loggedInUser.school, {
        name,
        departureWindow: { from: from.trim(), to: to.trim() },
        busId: busId || undefined,
        type,
        isDeleted: false
      });
      onAddRoute();
      onClose();
      showToast('Route added successfully!', SnackbarSeverityType.success);
    } catch (error) {
      showToast('Failed to add route.', SnackbarSeverityType.error);
    } finally {
      setIsLoading(false);
    }
  };

  const isAddButtonDisabled = !name || !departureWindow || !type || isLoading;

  return (
    <DialogModal
      open={open}
      onClose={onClose}
      title='Add A New Route'
      actions={
        <>
          <Button
            onClick={onClose}
            sx={{ border: `1px solid ${pallet.primary}` }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddRoute}
            disabled={isAddButtonDisabled}
            variant='contained'
          >
            {isLoading ? (
              <CircularProgress size={24} color='inherit' />
            ) : (
              'Submit'
            )}
          </Button>
        </>
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
        <FormControl>
          <FormLabel
            sx={{
              mb: 0.5,
              color: pallet.formLabelText,
              fontWeight: 600
            }}
            required
          >
            Route Type
          </FormLabel>
          <RadioGroup
            row
            value={type}
            onChange={(e) => setType(e.target.value as RouteType)}
            sx={{ gap: 2 }}
          >
            <StyledRadioButton
              value={RouteType.PICK_UP_ROUTE}
              control={<Radio />}
              label='Pick up Route'
            />
            <StyledRadioButton
              value={RouteType.DROP_OFF_ROUTE}
              control={<Radio />}
              label='Drop off Route'
            />
          </RadioGroup>
        </FormControl>
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
          <TextInputWithIcon
            label='Route Name'
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder='Enter a Route Name'
            required
            fullWidth
            sx={{ flex: 1 }}
          />
          <DropdownSelect
            label='Departure Window'
            placeholder='Select Departure Window'
            name='departureWindow'
            value={departureWindow}
            options={departureWindowOptions.map((option) => ({
              value: option,
              label: option
            }))}
            onChange={(e) => setDepartureWindow(e.target.value as string)}
            required
            fullWidth
            sx={{ flex: 1 }}
          />
        </Box>
        <DropdownSelect
          label='Assign Bus'
          placeholder='Select Bus'
          name='busId'
          value={busId || ''}
          options={busOptions}
          onChange={(e) => setBusId(e.target.value as string)}
        />
      </Box>
    </DialogModal>
  );
};

export default AddRouteDialog;
